/**
 * Query params that describe where an unauthenticated visitor was headed and how the public pages
 * should look for them.
 *
 * They are declared in this leaf module (deliberately import-free) so both the React hooks that own
 * their behaviour and plain modules can share one definition: `serverLogOut` has to rebuild a /login
 * URL out of them and cannot import `useAppLaunch` / `useBrandingConfig` without pulling the axios
 * layer back into AuthUtils (import cycle). This module is therefore the single place to import them
 * from — `useAppLaunch` / `useBrandingConfig` consume them like everyone else and deliberately do not
 * re-export them, so there is only one import path to keep straight.
 */
export const BRANDING_ID_PARAM = "brandingId";
export const BRANDING_MODE_PARAM = "mode";
export const OPEN_APP_PARAM = "openApp";
export const REDIRECT_URI_PARAM = "redirectUri";

/**
 * The params a forced return to /login has to carry (NG-8591). When a revoked session is discovered
 * on a deep link — /login?openApp=Patient+Vault&brandingId=<guid>&mode=dark — the logout redirect
 * dropped the whole query string, so the visitor re-authenticated into the dashboard in default
 * branding instead of the app they came to open.
 *
 * `redirectUri` rides along with `openApp` because Login treats the two as one deep link (redirectUri
 * wins); preserving one and silently dropping the other would just move the surprise.
 */
const PRESERVED_ON_LOGOUT = [OPEN_APP_PARAM, REDIRECT_URI_PARAM, BRANDING_ID_PARAM, BRANDING_MODE_PARAM] as const;

/**
 * Rebuild `path` carrying only the preserved params present in `search` (a `window.location.search`
 * string). Returns the bare path when there is nothing to carry — which is the case for every
 * sign-out from inside the app, so a deliberate logout is unaffected.
 */
export const withPreservedEntryParams = (path: string, search: string): string => {
    const source = new URLSearchParams(search);
    const preserved = new URLSearchParams();
    PRESERVED_ON_LOGOUT.forEach((key) => {
        const value = source.get(key);
        if (value) {
            preserved.set(key, value);
        }
    });
    const query = preserved.toString();
    return query ? `${path}?${query}` : path;
};
